import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fadeIn } from '../../utils/gsapAnimations';
import { fetchCustomerOrderDeliveryStatus } from '../../utils/api';

const ORDERS_PER_PAGE = 8;

const money = (value) =>
  new Intl.NumberFormat('en-SL', {
    style: 'currency',
    currency: 'SLE',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
  
const label = (value) =>
  String(value || 'unknown').replaceAll('_', ' ');

const asBoolean = (value) =>
  value === true || value === 1 || value === '1';

const statusStyle = (status) => {
  if (status === 'delivered' || status === 'successful' || status === 'paid') {
    return 'bg-green-50 text-green-700 border-green-200';
  }
  if (status === 'arrived') return 'bg-purple-50 text-purple-700 border-purple-200';
  if (status === 'in_transit' || status === 'picked_up') {
    return 'bg-blue-50 text-blue-700 border-blue-200';
  }
  return 'bg-amber-50 text-amber-700 border-amber-200';
};

const deliveryStageFrom = (live) => {
  if (!live) return 'unavailable';
  if (live.delivery_status) return live.delivery_status;
  if (asBoolean(live.is_delivered)) return 'delivered';
  if (asBoolean(live.has_arrived)) return 'arrived';
  if (asBoolean(live.package_picked)) return 'picked_up';
  return 'awaiting_pickup';
};

const mergeLiveDelivery = (order, live) => {
  if (!live) return order;

  const deliveryStatus = deliveryStageFrom(live);

  return {
    ...order,
    delivery_status: deliveryStatus,
    delivery: {
      ...(order?.delivery || {}),
      status: deliveryStatus,
      package_picked: asBoolean(live.package_picked),
      has_arrived: asBoolean(live.has_arrived),
      is_delivered: asBoolean(live.is_delivered),
      arrived_at: live.arrived_at || null,
      delivered_at: live.delivered_at || null,
      delivery_time: live.delivery_time || null,
    },
    contact: {
      ...(order?.contact || {}),
      name: live.contact_name || order?.contact?.name,
      email: live.delivery_email || order?.contact?.email,
      phone: live.phone_number || order?.contact?.phone,
      address: live.contact_address || order?.contact?.address,
    },
  };
};

const Detail = ({ title, value, link }) => (
  <div className="flex items-start justify-between gap-5 border-b border-gray-100 py-3 last:border-0">
    <span className="text-sm text-gray-500">{title}</span>
    {link && value ? (
      <a href={link} className="max-w-[65%] break-words text-right text-sm font-semibold text-primary hover:underline">
        {value}
      </a>
    ) : (
      <span className="max-w-[65%] break-words text-right text-sm font-semibold text-gray-800">
        {value || '—'}
      </span>
    )}
  </div>
);

function OrderDetailsModal({
  order,
  onClose,
  trackingLoading,
  trackingError,
  onRefreshTracking,
}) {
  if (!order) return null;

  const stages = [
    { key: 'placed', title: 'Order placed', complete: true },
    {
      key: 'picked_up',
      title: 'Package picked up',
      complete:
        order.delivery?.package_picked ||
        order.delivery?.has_arrived ||
        order.delivery?.is_delivered,
    },
    {
      key: 'arrived',
      title: 'Rider arrived',
      complete: order.delivery?.has_arrived || order.delivery?.is_delivered,
    },
    {
      key: 'delivered',
      title: 'Delivered',
      complete: order.delivery?.is_delivered,
    },
  ];

  return (
    <div className="fixed inset-x-0 bottom-0 top-16 z-[9999] flex items-start justify-center bg-black/60 p-3 backdrop-blur-sm sm:top-20 sm:p-5">
      <div className="h-[calc(100vh-5rem)] w-full max-w-7xl overflow-y-auto rounded-3xl bg-[#fbfaf7] shadow-2xl sm:h-[calc(100vh-6.5rem)]">
        <div className="sticky top-0 z-30 flex items-start justify-between gap-4 border-b border-black/5 bg-white px-5 py-5 shadow-sm sm:px-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[.18em] text-primary">Transaction details</p>
            <h2 className="mt-1 font-lora text-2xl font-semibold text-burgundy sm:text-3xl">
              {order.merchant_name}
            </h2>
            <p className="mt-1 text-sm text-gray-500">{order.date}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="relative z-40 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-xl text-gray-600 shadow-sm hover:bg-gray-100"
            aria-label="Close transaction details"
          >
            ×
          </button>
        </div>

        <div className="space-y-6 p-5 sm:p-8">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-2xl bg-burgundy p-5 text-white">
              <p className="text-xs uppercase tracking-wider text-white/65">Order total</p>
              <p className="mt-2 font-lora text-3xl font-semibold">{money(order.amount, order.currency)}</p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white p-5">
              <p className="text-xs uppercase tracking-wider text-gray-400">Payment</p>
              <span className={`mt-3 inline-flex rounded-full border px-3 py-1 text-xs font-bold capitalize ${statusStyle(order.payment?.status)}`}>
                {label(order.payment?.status)}
              </span>
              <p className="mt-3 text-sm capitalize text-gray-600">{label(order.payment?.method)}</p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white p-5">
              <p className="text-xs uppercase tracking-wider text-gray-400">Delivery</p>
              <span className={`mt-3 inline-flex rounded-full border px-3 py-1 text-xs font-bold capitalize ${statusStyle(order.delivery_status)}`}>
                {label(order.delivery_status)}
              </span>
              <p className="mt-3 text-sm text-gray-600">{order.delivery?.delivery_time || 'Status is being monitored'}</p>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-5 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="font-lora text-xl font-semibold text-burgundy">Delivery journey</h3>
                <p className="mt-1 text-xs text-gray-500">
                  {trackingLoading
                    ? 'Checking the restaurant for the latest status…'
                    : 'Live status supplied by the restaurant.'}
                </p>
              </div>
              <button
                type="button"
                onClick={onRefreshTracking}
                disabled={trackingLoading}
                className="rounded-full border border-primary/30 px-4 py-2 text-xs font-bold text-primary hover:bg-primary hover:text-white disabled:opacity-50"
              >
                {trackingLoading ? 'Refreshing…' : 'Refresh status'}
              </button>
            </div>
            {trackingError && (
              <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                {trackingError}
              </p>
            )}
            <div className="mt-6 grid gap-3 sm:grid-cols-4">
              {stages.map((stage, index) => (
                <div key={stage.key} className="relative">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold ${stage.complete ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'}`}>
                    {stage.complete ? '✓' : index + 1}
                  </div>
                  <p className={`mt-2 text-sm font-semibold ${stage.complete ? 'text-burgundy' : 'text-gray-400'}`}>
                    {stage.title}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-5">
            <div className="rounded-2xl border border-gray-100 bg-white p-5 sm:p-6 lg:col-span-3">
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-lora text-xl font-semibold text-burgundy">Items purchased</h3>
                <span className="text-sm text-gray-500">{order.items?.length || 0} lines</span>
              </div>
              <div className="mt-5 divide-y divide-gray-100">
                {(order.items || []).map((item, index) => (
                  <div key={`${item.product_id}-${index}`} className="flex items-center justify-between gap-4 py-4">
                    <div>
                      <p className="font-semibold text-gray-800">{item.product_name}</p>
                      <p className="mt-1 text-xs text-gray-500">{money(item.price, order.currency)} × {item.qty}</p>
                    </div>
                    <strong className="text-burgundy">{money(item.total, order.currency)}</strong>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-2xl bg-cream/60 p-4">
                <Detail title="VAT" value={money(order.fees?.vat, order.currency)} />
                <Detail title="Delivery fee" value={money(order.fees?.shipping, order.currency)} />
                <Detail title="Discount" value={money(order.fees?.discount, order.currency)} />
                <Detail title="Total" value={money(order.fees?.total, order.currency)} />
              </div>
            </div>

            <div className="space-y-6 lg:col-span-2">
              <div className="rounded-2xl border border-gray-100 bg-white p-5">
                <h3 className="font-lora text-lg font-semibold text-burgundy">Delivery contact</h3>
                <div className="mt-3">
                  <Detail title="Name" value={order.contact?.name} />
                  <Detail title="Email" value={order.contact?.email} link={order.contact?.email ? `mailto:${order.contact.email}` : null} />
                  <Detail title="Phone" value={order.contact?.phone} link={order.contact?.phone ? `tel:${order.contact.phone}` : null} />
                  <Detail title="Address" value={order.contact?.address} />
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-white p-5">
                <h3 className="font-lora text-lg font-semibold text-burgundy">References</h3>
                <div className="mt-3">
                  <Detail title="Order ID" value={order.transaction_id} />
                  <Detail title="Log ID" value={order.id} />
                  <Detail title="UUID" value={order.uuid} />
                  <Detail title="Customer ID" value={order.customer_id} />
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-white p-5">
                <h3 className="font-lora text-lg font-semibold text-burgundy">Restaurant</h3>
                <div className="mt-3">
                  <Detail title="Business" value={order.merchant?.business_name} />
                  <Detail title="Address" value={order.merchant?.business_address} />
                  <Detail title="Phone" value={order.merchant?.phone_number} link={order.merchant?.phone_number ? `tel:${order.merchant.phone_number}` : null} />
                  <Detail title="Email" value={order.merchant?.business_email} link={order.merchant?.business_email ? `mailto:${order.merchant.business_email}` : null} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrderHistory({ orders = [], deliverySummary = {}, loading = false }) {
  const sectionRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState('');
  const [liveStatuses, setLiveStatuses] = useState({});
  const [syncingVisibleOrders, setSyncingVisibleOrders] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fadeIn(sectionRef.current, 0.3);
  }, []);

  const filteredOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return orders;

    return orders.filter((order) =>
      [order.uuid, order.transaction_id, order.merchant_name, order.delivery_status]
        .some((value) => String(value || '').toLowerCase().includes(term))
    );
  }, [orders, search]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / ORDERS_PER_PAGE));
  const visibleOrders = filteredOrders.slice(
    (currentPage - 1) * ORDERS_PER_PAGE,
    currentPage * ORDERS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, orders]);

  const refreshDeliveryStatus = useCallback(async (order, updateModal = true) => {
    if (!order) return;

    if (updateModal) {
      setTrackingLoading(true);
      setTrackingError('');
    }

    setLiveStatuses((current) => ({
      ...current,
      [order.id]: {
        ...(current[order.id] || {}),
        status: 'loading',
      },
    }));

    try {
      const live = await fetchCustomerOrderDeliveryStatus(order);
      const mergedOrder = mergeLiveDelivery(order, live);

      setLiveStatuses((current) => ({
        ...current,
        [order.id]: {
          status: mergedOrder.delivery_status,
          data: live,
          error: '',
        },
      }));

      if (updateModal) {
        setSelectedOrder(mergedOrder);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'The latest delivery status could not be retrieved.';

      setLiveStatuses((current) => ({
        ...current,
        [order.id]: {
          status: 'unavailable',
          data: null,
          error: message,
        },
      }));

      if (updateModal) {
        setTrackingError(message);
      }
    } finally {
      if (updateModal) {
        setTrackingLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const unsyncedOrders = visibleOrders.filter(
      (order) => !liveStatuses[order.id]
    );

    if (!unsyncedOrders.length) return;

    let active = true;
    setSyncingVisibleOrders(true);

    Promise.allSettled(
      unsyncedOrders.map((order) =>
        refreshDeliveryStatus(order, false)
      )
    ).finally(() => {
      if (active) setSyncingVisibleOrders(false);
    });

    return () => {
      active = false;
    };
  }, [visibleOrders, liveStatuses, refreshDeliveryStatus]);

  const openOrder = (order) => {
    const live = liveStatuses[order.id]?.data;
    setSelectedOrder(mergeLiveDelivery(order, live));

    // Always request the merchant's current delivery details when the modal
    // opens. The cached value is only used to avoid showing an empty modal
    // while the fresh request is running.
    refreshDeliveryStatus(order, true);
  };

  return (
    <div ref={sectionRef} className="space-y-5">
      {/* <div className="grid grid-cols-3 gap-3">
        {[
          ['Delivered', deliverySummary.delivered, 'text-green-700 bg-green-50'],
          ['In transit', deliverySummary.in_transit, 'text-blue-700 bg-blue-50'],
          ['Preparing', deliverySummary.awaiting_pickup, 'text-amber-700 bg-amber-50'],
        ].map(([title, value, classes]) => (
          <div key={title} className={`rounded-2xl p-4 text-center ${classes}`}>
            <p className="text-2xl font-bold">{Number(value || 0)}</p>
            <p className="mt-1 text-xs font-semibold">{title}</p>
          </div>
        ))}
      </div> */}

      <div className="rounded-3xl border border-gray-100 bg-white shadow-xl">
        <div className="flex flex-col gap-4 border-b border-gray-100 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div>
            <h2 className="font-lora text-2xl font-semibold text-burgundy">Order history</h2>
            <p className="mt-1 text-sm text-gray-500">
              {syncingVisibleOrders
                ? 'Synchronizing delivery details with the restaurants…'
                : 'Every marketplace transaction linked to your customer account.'}
            </p>
          </div>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search order or restaurant"
            className="min-h-[44px] rounded-full border border-gray-200 px-5 text-sm outline-none focus:border-primary sm:w-72"
          />
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading your transactions...</div>
        ) : visibleOrders.length === 0 ? (
          <div className="p-12 text-center">
            <p className="font-semibold text-burgundy">No matching orders</p>
            <p className="mt-1 text-sm text-gray-500">Completed purchases will appear here.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {visibleOrders.map((order) => {
              const liveStatus = liveStatuses[order.id]?.status;
              const displayedStatus =
                liveStatus === 'loading'
                  ? 'loading'
                  : liveStatus || order.delivery_status;

              return (
              <button
                type="button"
                key={order.id}
                onClick={() => openOrder(order)}
                className="grid w-full gap-3 p-5 text-left transition hover:bg-cream/40 sm:grid-cols-[1.4fr_1fr_1fr_auto] sm:items-center sm:px-6"
              >
                <div>
                  <p className="font-semibold text-burgundy">{order.merchant_name}</p>
                  <p className="mt-1 text-xs text-gray-500">Order #{order.transaction_id || order.id} · {order.date}</p>
                </div>
                <div>
                  <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-bold capitalize ${statusStyle(displayedStatus)}`}>
                    {displayedStatus === 'loading'
                      ? 'Checking…'
                      : label(displayedStatus)}
                  </span>
                </div>
                <strong className="text-burgundy">{money(order.amount, order.currency)}</strong>
                <span className="text-sm font-semibold text-primary">View details →</span>
              </button>
              );
            })}

          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 p-5">
            <button type="button" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => page - 1)} className="rounded-full border px-4 py-2 text-sm disabled:opacity-40">Previous</button>
            <span className="text-sm text-gray-500">Page {currentPage} of {totalPages}</span>
            <button type="button" disabled={currentPage === totalPages} onClick={() => setCurrentPage((page) => page + 1)} className="rounded-full border px-4 py-2 text-sm disabled:opacity-40">Next</button>
          </div>
        )}
      </div>

      <OrderDetailsModal
        order={selectedOrder}
        onClose={() => {
          setSelectedOrder(null);
          setTrackingError('');
        }}
        trackingLoading={trackingLoading}
        trackingError={trackingError}
        onRefreshTracking={() => refreshDeliveryStatus(selectedOrder)}
      />
    </div>
  );
}

export default OrderHistory;
